import { User, Users, ClipboardCheck, TrendingUp, Search, FileText, Repeat, BarChart3, Crown, Sparkles, Shield, Linkedin } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import PageHero from "@/components/shared/PageHero";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import FinalCTA from "@/components/shared/FinalCTA";
import ProtectedImage from "@/components/shared/ProtectedImage";
import { SITE } from "@/lib/siteConfig";

const EXEC_TOPICS = [
  "Leadership effectiveness & executive presence",
  "Strategic decision-making under pressure",
  "Communication & influencing skills",
  "Leading through change and uncertainty",
  "Managing performance and difficult conversations",
  "Building resilience and avoiding burnout",
];

const EXEC_FORMATS = [
  { icon: User, text: "1:1 Executive Coaching (in-person or online)" },
  { icon: Users, text: "Leadership Team Coaching" },
  { icon: ClipboardCheck, text: "360° Feedback & Development Planning" },
  { icon: TrendingUp, text: "Ongoing Leadership Advisory Retainer" },
];

const LIFE_TOPICS = [
  "Career clarity and direction",
  "Building confidence and self-belief",
  "Managing transitions and change",
  "Goal-setting and accountability",
  "Work-life harmony and wellbeing",
  "Overcoming limiting beliefs",
];

const PROCESS = [
  {
    icon: Search,
    title: "Discovery Conversation",
    badge: "Free",
    text: "We start by listening. Understanding where you are, where you want to be and what's getting in the way.",
  },
  {
    icon: FileText,
    title: "Tailored Coaching Plan",
    text: "A structured, personalised programme built around your specific goals and timeline.",
  },
  {
    icon: Repeat,
    title: "Regular Coaching Sessions",
    text: "Consistent, focused sessions that create momentum and real, measurable progress.",
  },
  {
    icon: BarChart3,
    title: "Measurable Outcomes",
    text: "We track progress against your goals, because good coaching produces real results.",
  },
];

const FAQS = [
  {
    q: "How many sessions do I need?",
    a: "Every coaching journey is different. We typically recommend a minimum of 6 sessions for meaningful progress, with many clients continuing for 3 to 6 months.",
  },
  {
    q: "Is coaching available online?",
    a: "Yes. All coaching is available online via video call, in-person in the Netherlands, or a combination of both.",
  },
  {
    q: "How is coaching different from therapy or consulting?",
    a: "Coaching focuses on forward momentum: your goals, your growth, your future. We don't diagnose or treat; we challenge, support and guide.",
  },
  {
    q: "Who are your coaches?",
    a: "Coaching is led by Ashutosh Pandey, with deep experience in both executive leadership and personal development across international environments.",
  },
];

function Track({ eyebrow, title, intro, topics, image, imageAlt, formats }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-testid={`coaching-track-${eyebrow.toLowerCase().includes("leader") ? "exec" : "life"}`}>
      <div className="lg:col-span-7">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C9A84C] mb-4">
          {eyebrow}
        </div>
        <h3 className="font-display text-3xl md:text-4xl text-[#1E2761] tracking-tight mb-5">
          {title}
        </h3>
        <p className="text-base md:text-lg text-[#334155]/85 leading-relaxed mb-8">
          {intro}
        </p>
        <div className="mb-8">
          <div className="text-sm font-semibold text-[#1E2761] mb-3">What we work on:</div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {topics.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-[#334155]">
                <span className="mt-2 w-1.5 h-1.5 bg-[#C9A84C] rounded-full flex-shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>
        {formats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {formats.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.text} className="flex items-center gap-3 p-4 bg-[#F4F7FC] border-l-2 border-[#C9A84C]">
                  <Icon size={16} className="text-[#1E2761] flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-sm text-[#334155]">{f.text}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="lg:col-span-5">
        <div className="relative aspect-[4/5] overflow-hidden">
          <img src={image} alt={imageAlt} className="w-full h-full object-cover" />
          <div className="absolute -bottom-4 -left-4 w-20 h-20 border-l-2 border-b-2 border-[#C9A84C]" />
        </div>
      </div>
    </div>
  );
}

export default function Coaching() {
  return (
    <div data-testid="coaching-page">
      <PageHero
        eyebrow="Executive & Leadership Coaching"
        headline="Real Growth Starts with Real Conversations."
        subheadline="Coaching built for senior leaders and ambitious professionals. Confidential space to think clearly, decide well and lead with authentic confidence, whether you are stepping up, stepping back or stepping into something new."
        ctaLabel="Book Your First Coaching Session"
        image={SITE.coachImage}
        imageAlt="Ashutosh Pandey, Executive and Leadership Coach at NextChapter4You"
        imageProtected={true}
      />

      {/* ABOUT YOUR COACH — quick trust callout */}
      <section className="bg-white py-14 md:py-16 px-6 md:px-10 border-b border-[#E2E8F0]" data-testid="about-coach-callout">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
            {/* Circular avatar */}
            <div className="relative flex-shrink-0">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden ring-4 ring-[#C9A84C] ring-offset-4 ring-offset-white">
                <ProtectedImage
                  src={SITE.founderImage}
                  alt="Ashutosh Pandey, Executive and Leadership Coach"
                  imgClassName="w-full h-full object-cover object-top"
                  watermark=""
                />
              </div>
              <div className="absolute -bottom-1 -right-1 bg-[#1E2761] text-[#C9A84C] w-8 h-8 rounded-full flex items-center justify-center border-2 border-white">
                <Crown size={14} strokeWidth={2} />
              </div>
            </div>

            {/* Bio + CTAs */}
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.2em] text-[#C9A84C] mb-2">
                Your Coach
              </div>
              <h3 className="font-display text-xl md:text-2xl text-[#1E2761] tracking-tight mb-3">
                Ashutosh Pandey <span className="text-[#334155]/60 font-normal text-base">— Executive & Leadership Coach</span>
              </h3>
              <p className="text-sm md:text-base text-[#334155]/85 leading-relaxed mb-4 max-w-3xl">
                Two decades of leading teams and mentoring senior professionals
                across Europe. Coaching senior leaders through complexity,
                transitions and stretch moments, with the calm honesty that
                only comes from having sat in the seat yourself.
              </p>
              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                <span className="inline-flex items-center text-xs uppercase tracking-[0.16em] text-[#1E2761] bg-[#F4F7FC] border border-[#E2E8F0] px-3 py-1.5">
                  Banking & Logistics
                </span>
                <span className="inline-flex items-center text-xs uppercase tracking-[0.16em] text-[#1E2761] bg-[#F4F7FC] border border-[#E2E8F0] px-3 py-1.5">
                  Public Sector
                </span>
                <a
                  href={SITE.linkedinBrand}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="coach-callout-linkedin"
                  className="ml-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#1E2761] border border-[#1E2761] px-3 py-1.5 hover:bg-[#1E2761] hover:text-white transition-colors"
                >
                  <Linkedin size={13} strokeWidth={1.7} />
                  Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEADERSHIP FOCUS BAND */}
      <section className="bg-[#F4F7FC] py-20 md:py-24 px-6 md:px-10" data-testid="leadership-focus">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14">
            <SectionEyebrow className="mb-4">Our Signature Focus</SectionEyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-[#1E2761] tracking-tight leading-tight">
              Coaching Senior Leaders is What We Do Best.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#334155]/85 leading-relaxed">
              Most of our work is with directors, VPs, founders and functional
              heads. People carrying real weight who need one honest
              conversation partner outside the noise of daily operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {[
              {
                icon: Crown,
                num: "01",
                title: "Executive Presence & Voice",
                text: "Show up in every room as the leader people want to follow, grounded, clear and calm under pressure.",
              },
              {
                icon: Shield,
                num: "02",
                title: "Decisions Under Complexity",
                text: "Cut through ambiguity, weigh trade-offs faster and back yourself when the data is incomplete.",
              },
              {
                icon: Sparkles,
                num: "03",
                title: "Sustainable Performance",
                text: "Lead at your best without burning out. Build the habits, boundaries and clarity that keep you effective for the long term.",
              },
            ].map((d) => {
              const Icon = d.icon;
              return (
                <div
                  key={d.num}
                  data-testid={`leadership-outcome-${d.num}`}
                  className="bg-white p-8 md:p-10 min-h-[240px] flex flex-col hover:bg-[#F4F7FC] transition-colors"
                >
                  <div className="flex items-baseline gap-3 mb-5">
                    <span className="font-display text-4xl text-[#C9A84C]">
                      {d.num}
                    </span>
                    <Icon size={20} className="text-[#1E2761]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-lg md:text-xl text-[#1E2761] mb-3 tracking-tight">
                    {d.title}
                  </h3>
                  <p className="text-sm text-[#334155]/80 leading-relaxed">
                    {d.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32 px-6 md:px-10" data-testid="coaching-tracks">
        <div className="max-w-7xl mx-auto space-y-28">
          <Track
            eyebrow="For Leaders & Organisations"
            title="Executive & Leadership Coaching"
            intro="Great leadership isn't accidental. It's built through honest reflection, skilled challenge and consistent practice. Our executive coaching helps leaders navigate complexity, lead with authenticity and deliver results that last."
            topics={EXEC_TOPICS}
            formats={EXEC_FORMATS}
            image="https://images.pexels.com/photos/5439137/pexels-photo-5439137.jpeg"
            imageAlt="Executive one-on-one coaching session"
          />
          <div className="relative">
            <div className="absolute left-1/2 -translate-x-1/2 -top-14 h-12 w-[1px] bg-[#C9A84C]/40" />
          </div>
          <Track
            eyebrow="For Individuals"
            title="Personal & Life Coaching"
            intro="Sometimes the biggest professional challenges are also deeply personal ones. Our life coaching creates a confidential, empathetic space to find clarity, rebuild confidence and move forward with purpose."
            topics={LIFE_TOPICS}
            image="https://images.pexels.com/photos/6932049/pexels-photo-6932049.jpeg"
            imageAlt="Personal growth and reflection in a calm space"
          />
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#F4F7FC] py-24 md:py-32 px-6 md:px-10" data-testid="coaching-process">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 max-w-2xl">
            <SectionEyebrow className="mb-4">The Coaching Process</SectionEyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-[#1E2761] tracking-tight">
              How We Work Together
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {PROCESS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="bg-white p-8 md:p-10 relative">
                  <div className="font-display text-6xl text-[#C9A84C]/20 absolute top-4 right-6">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="w-12 h-12 border border-[#C9A84C] flex items-center justify-center mb-5 text-[#C9A84C] relative">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-lg text-[#1E2761] mb-2 tracking-tight flex items-center gap-2">
                    {p.title}
                    {p.badge && (
                      <span className="text-[10px] uppercase tracking-wider bg-[#C9A84C] text-[#1E2761] px-2 py-0.5 font-semibold">
                        {p.badge}
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-[#334155]/80 leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST STATEMENT */}
      <section className="bg-white py-20 md:py-28 px-6 md:px-10" data-testid="trust-statement">
        <div className="max-w-4xl mx-auto bg-[#FBF8F0] border-l-4 border-[#C9A84C] p-10 md:p-14">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A84C] mb-4">
            Our Commitment
          </div>
          <p className="font-display text-2xl md:text-3xl text-[#1E2761] leading-snug tracking-tight">
            Coaching at NextChapter4You is built on three commitments:{" "}
            <span className="italic">complete confidentiality</span>,{" "}
            <span className="italic">genuine empathy</span>, and{" "}
            <span className="italic">honest challenge</span>.
          </p>
          <p className="mt-6 text-base text-[#334155]/85 leading-relaxed">
            We are not here to tell you what you want to hear. We are here to
            help you become what you are capable of.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F4F7FC] py-24 md:py-32 px-6 md:px-10" data-testid="coaching-faq">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <SectionEyebrow className="mb-4">Frequently Asked</SectionEyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-[#1E2761] tracking-tight">
              Things People Often Ask
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-white border border-[#E2E8F0] data-[state=open]:border-[#C9A84C]/60 transition-colors px-6"
              >
                <AccordionTrigger className="font-display text-lg text-[#1E2761] tracking-tight hover:no-underline py-5 text-left">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#334155]/85 text-base leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <FinalCTA
        headline="Ready to Start the Conversation?"
        subtext="Your first discovery conversation is on us. Tell us what you're working through, we'll listen first, then help you map the way forward."
        ctaLabel="Book a Free Discovery Call"
      />
    </div>
  );
}
